"use client";

import { useState, useCallback, useMemo } from "react";

interface POSLineItem {
  menuItemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  modifiers: Array<{ modifierId: string; name: string; price: number }>;
  notes: string;
}

interface POSState {
  orderType: string;
  tableId: string | null;
  tableNumber: string | null;
  customerId: string | null;
  guestCount: number;
  items: POSLineItem[];
  notes: string;
}

const INITIAL_STATE: POSState = {
  orderType: "DINE_IN",
  tableId: null,
  tableNumber: null,
  customerId: null,
  guestCount: 1,
  items: [],
  notes: "",
};

export function usePOS(taxRate = 0.0875) {
  const [state, setState] = useState<POSState>(INITIAL_STATE);
  const [sendingToKitchen, setSendingToKitchen] = useState(false);

  const addItem = useCallback((item: {
    menuItemId: string;
    name: string;
    price: number;
    modifiers?: Array<{ modifierId: string; name: string; price: number }>;
    notes?: string;
  }) => {
    setState((prev) => {
      const modifiers = item.modifiers || [];
      const modifierTotal = modifiers.reduce((sum, m) => sum + m.price, 0);
      const unitPrice = item.price + modifierTotal;

      // Check if same item with same modifiers already exists
      const existingIndex = prev.items.findIndex(
        (i) =>
          i.menuItemId === item.menuItemId &&
          i.notes === (item.notes || "") &&
          JSON.stringify(i.modifiers.map((m) => m.modifierId).sort()) ===
            JSON.stringify(modifiers.map((m) => m.modifierId).sort())
      );

      if (existingIndex >= 0) {
        const updated = [...prev.items];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
          totalPrice: (updated[existingIndex].quantity + 1) * unitPrice,
        };
        return { ...prev, items: updated };
      }

      return {
        ...prev,
        items: [
          ...prev.items,
          {
            menuItemId: item.menuItemId,
            name: item.name,
            quantity: 1,
            unitPrice,
            totalPrice: unitPrice,
            modifiers,
            notes: item.notes || "",
          },
        ],
      };
    });
  }, []);

  const updateQuantity = useCallback((index: number, quantity: number) => {
    setState((prev) => {
      if (quantity <= 0) {
        return { ...prev, items: prev.items.filter((_, i) => i !== index) };
      }
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        quantity,
        totalPrice: quantity * updated[index].unitPrice,
      };
      return { ...prev, items: updated };
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  }, []);

  const clearOrder = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const setOrderType = useCallback((orderType: string) => {
    setState((prev) => ({
      ...prev,
      orderType,
      tableId: orderType === "DINE_IN" ? prev.tableId : null,
      tableNumber: orderType === "DINE_IN" ? prev.tableNumber : null,
    }));
  }, []);

  const setTable = useCallback((tableId: string | null, tableNumber: string | null) => {
    setState((prev) => ({ ...prev, tableId, tableNumber }));
  }, []);

  const setCustomer = useCallback((customerId: string | null) => {
    setState((prev) => ({ ...prev, customerId }));
  }, []);

  const setGuestCount = useCallback((guestCount: number) => {
    setState((prev) => ({ ...prev, guestCount }));
  }, []);

  const setNotes = useCallback((notes: string) => {
    setState((prev) => ({ ...prev, notes }));
  }, []);

  const subtotal = useMemo(
    () => state.items.reduce((sum, item) => sum + item.totalPrice, 0),
    [state.items]
  );

  const taxAmount = useMemo(() => Math.round(subtotal * taxRate), [subtotal, taxRate]);

  const total = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  const itemCount = useMemo(
    () => state.items.reduce((sum, item) => sum + item.quantity, 0),
    [state.items]
  );

  const sendToKitchen = useCallback(async () => {
    if (state.items.length === 0) return null;

    try {
      setSendingToKitchen(true);
      // Create the order
      const createRes = await fetch("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: state.orderType,
          tableId: state.tableId,
          customerId: state.customerId,
          guestCount: state.guestCount,
          notes: state.notes,
          items: state.items.map((item) => ({
            menuItemId: item.menuItemId,
            quantity: item.quantity,
            notes: item.notes || undefined,
            modifiers: item.modifiers.map((m) => ({ modifierId: m.modifierId })),
          })),
        }),
      });

      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.error || "Failed to create order");
      }

      const order = await createRes.json();
      const orderId = order.data?.id || order.id;

      // Transition to SENT_TO_KITCHEN
      const transitionRes = await fetch(`/api/orders/${orderId}/transition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "SENT_TO_KITCHEN" }),
      });

      if (!transitionRes.ok) {
        const err = await transitionRes.json();
        throw new Error(err.error || "Failed to send to kitchen");
      }

      // Clear the POS state
      setState(INITIAL_STATE);
      return order;
    } finally {
      setSendingToKitchen(false);
    }
  }, [state]);

  return {
    ...state,
    subtotal,
    taxAmount,
    total,
    itemCount,
    sendingToKitchen,
    addItem,
    updateQuantity,
    removeItem,
    clearOrder,
    setOrderType,
    setTable,
    setCustomer,
    setGuestCount,
    setNotes,
    sendToKitchen,
  };
}
